import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, ImagePlus, Car as CarIcon, Tag, Edit3, X, Check, ExternalLink } from "lucide-react";
import { vehiclePhotoUrl, formatPrice } from "@/pages/VehicleListPage";

const BLANK = {
  title: "", status: "sale", make: "", model: "", year: new Date().getFullYear(),
  price: 0, description: "",
  kilometres: "", body_type: "", transmission: "", fuel: "", rego_status: "", vin: "",
  weekly_rate: "", bond_amount: 500, min_rent_days: 7, max_rent_days: "",
  is_featured: false,
};

const BODY_OPTS = [["sedan","Sedan"],["suv","SUV"],["ute","Ute"],["hatch","Hatch"],["wagon","Wagon"],["van","Van"],["coupe","Coupe"],["convertible","Convertible"],["other","Other"]];
const TRANS_OPTS = [["auto","Automatic"],["manual","Manual"]];
const FUEL_OPTS = [["petrol","Petrol"],["diesel","Diesel"],["hybrid","Hybrid"],["ev","Electric"],["lpg","LPG"]];
const REGO_OPTS = [["active","Registered"],["expired","Rego expired"],["unregistered","Unregistered"]];

export default function InventoryManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | {id, ...vehicle}

  const load = async () => {
    try {
      const r = await api.get("/admin/vehicles");
      setItems(r.data);
    } catch (e) {
      toast.error("Could not load inventory");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((v) => v.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 border-l-2 border-brand-red pl-4 mb-2">
        <CarIcon className="h-5 w-5 text-brand-red mt-0.5" />
        <div>
          <h3 className="font-display uppercase text-xl text-white tracking-tight">Vehicle inventory</h3>
          <p className="text-sm text-zinc-400 max-w-xl mt-1">Manage cars for sale and for rent. Add photos, prices, specs and descriptions — changes go live immediately.</p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {["all", "sale", "rent"].map((f) => (
            <button
              key={f}
              data-testid={`inv-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border ${filter === f ? "border-brand-red text-brand-red bg-brand-red/10" : "border-zinc-800 text-zinc-400 hover:text-white"}`}
            >
              {f === "all" ? `All (${items.length})` : f === "sale" ? `For Sale (${items.filter((v) => v.status === "sale").length})` : `For Rent (${items.filter((v) => v.status === "rent").length})`}
            </button>
          ))}
        </div>
        <button data-testid="inv-add" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 px-5 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B]">
          <Plus className="h-3.5 w-3.5" /> Add vehicle
        </button>
      </div>

      {editing && (
        <VehicleEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(v) => {
            setItems((prev) => {
              const found = prev.find((p) => p.id === v.id);
              return found ? prev.map((p) => (p.id === v.id ? v : p)) : [v, ...prev];
            });
            setEditing(v);
          }}
        />
      )}

      {filtered.length === 0 ? (
        <div className="border border-zinc-900 bg-zinc-950 p-10 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest">
          No vehicles {filter !== "all" ? `for ${filter}` : "yet"}. Click &quot;Add vehicle&quot; to list your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => (
            <InventoryCard
              key={v.id}
              v={v}
              onEdit={() => setEditing(v)}
              onDeleted={(id) => setItems((prev) => prev.filter((p) => p.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InventoryCard({ v, onEdit, onDeleted }) {
  const remove = async () => {
    if (!window.confirm(`Delete "${v.title}"? This removes it and all its photos.`)) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      onDeleted(v.id);
      toast.success("Vehicle removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };
  return (
    <article data-testid={`inv-card-${v.id}`} className="border border-zinc-900 bg-zinc-950 overflow-hidden">
      <div className="aspect-[16/10] bg-zinc-900 relative">
        {v.cover_photo ? (
          <img src={vehiclePhotoUrl(v.cover_photo)} alt={v.title} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center text-zinc-700"><ImagePlus className="h-8 w-8" /></div>
        )}
        <span className={`absolute top-2 left-2 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest ${v.status === "rent" ? "border-blue-500 text-blue-300 bg-blue-950/40" : "border-brand-red text-brand-red bg-brand-red/10"}`}>
          {v.status === "rent" ? "Rent" : "Sale"}
        </span>
        {v.is_sold && <span className="absolute top-2 right-2 px-2 py-0.5 border border-zinc-700 bg-black/70 text-zinc-300 font-mono text-[10px] uppercase tracking-widest">Sold</span>}
        {!v.is_sold && v.is_featured && <span className="absolute top-2 right-2 px-2 py-0.5 border border-brand-red bg-brand-red text-white font-mono text-[10px] uppercase tracking-widest">★ Featured</span>}
      </div>
      <div className="p-4 space-y-3">
        <div>
          <h4 className="font-display uppercase text-base text-white tracking-tight">{v.title}</h4>
          <div className="text-brand-red font-display text-lg">
            {formatPrice(v.price)}{v.status === "rent" && <span className="text-zinc-500 text-xs">/day</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/vehicles/${v.id}`} target="_blank" rel="noopener noreferrer" data-testid={`inv-view-${v.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:text-white text-[10px] font-mono uppercase tracking-widest">
            <ExternalLink className="h-3 w-3" /> View
          </a>
          <button data-testid={`inv-edit-${v.id}`} onClick={onEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-brand-red hover:text-brand-red text-[10px] font-mono uppercase tracking-widest">
            <Edit3 className="h-3 w-3" /> Edit
          </button>
          <button data-testid={`inv-del-${v.id}`} onClick={remove} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-red-600 hover:text-red-400 text-[10px] font-mono uppercase tracking-widest">
            <Trash2 className="h-3 w-3" /> Delete
          </button>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{v.photos?.length || 0} photo(s)</div>
      </div>
    </article>
  );
}

function VehicleEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => initial ? {
    ...BLANK,
    ...initial,
    kilometres: initial.kilometres ?? "",
    body_type: initial.body_type ?? "",
    transmission: initial.transmission ?? "",
    fuel: initial.fuel ?? "",
    rego_status: initial.rego_status ?? "",
    vin: initial.vin ?? "",
    weekly_rate: initial.weekly_rate ?? "",
    bond_amount: initial.bond_amount ?? 500,
    min_rent_days: initial.min_rent_days ?? 7,
    max_rent_days: initial.max_rent_days ?? "",
  } : BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => {
    const p = { ...form };
    // Coerce empty strings on optional fields to null/undefined
    ["kilometres", "weekly_rate", "max_rent_days"].forEach((k) => {
      if (p[k] === "" || p[k] === null) delete p[k];
      else p[k] = Number(p[k]);
    });
    ["body_type", "transmission", "fuel", "rego_status", "vin"].forEach((k) => {
      if (!p[k]) delete p[k];
    });
    p.year = Number(p.year);
    p.price = Number(p.price);
    p.bond_amount = Number(p.bond_amount) || 0;
    p.min_rent_days = Number(p.min_rent_days) || 1;
    return p;
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = isNew
        ? await api.post("/admin/vehicles", payload)
        : await api.patch(`/admin/vehicles/${initial.id}`, payload);
      toast.success(isNew ? "Vehicle created — now add some photos" : "Vehicle updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!initial?.id) {
      toast.error("Save the vehicle first, then add photos.");
      return;
    }
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error("Only JPEG, PNG, WebP or GIF images are allowed");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be under 12 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/vehicles/${initial.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved(data);
      toast.success("Photo uploaded");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photoId) => {
    if (!initial?.id) return;
    if (!window.confirm("Remove this photo?")) return;
    try {
      const { data } = await api.delete(`/admin/vehicles/${initial.id}/photos/${photoId}`);
      onSaved(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="inv-editor" className="border border-brand-red/40 bg-zinc-950 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="font-display uppercase text-xl text-white tracking-tight">
          {isNew ? "New vehicle" : `Edit · ${initial.title}`}
        </h4>
        <button data-testid="inv-editor-close" onClick={onClose} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:text-white text-[10px] font-mono uppercase tracking-widest">
          <X className="h-3 w-3" /> Close
        </button>
      </div>

      <form onSubmit={save} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <FieldLabel cols={6} label="Title (shown on cards)">
            <input data-testid="inv-title" required className="ta-svc-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="2019 Toyota HiLux SR5 Auto" />
          </FieldLabel>
          <FieldLabel cols={3} label="Listing type">
            <select data-testid="inv-status" className="ta-svc-input" value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="sale">For sale</option>
              <option value="rent">For rent</option>
            </select>
          </FieldLabel>
          <FieldLabel cols={3} label={form.status === "rent" ? "Daily rate (AUD)" : "Price (AUD)"}>
            <input data-testid="inv-price" required type="number" min="0" step="1" className="ta-svc-input" value={form.price} onChange={(e) => set("price", e.target.value)} />
          </FieldLabel>

          <FieldLabel cols={4} label="Make"><input data-testid="inv-make" required className="ta-svc-input" value={form.make} onChange={(e) => set("make", e.target.value)} /></FieldLabel>
          <FieldLabel cols={5} label="Model"><input data-testid="inv-model" required className="ta-svc-input" value={form.model} onChange={(e) => set("model", e.target.value)} /></FieldLabel>
          <FieldLabel cols={3} label="Year"><input data-testid="inv-year" required type="number" min="1900" max="2100" className="ta-svc-input" value={form.year} onChange={(e) => set("year", e.target.value)} /></FieldLabel>

          <FieldLabel cols={4} label="Kilometres"><input data-testid="inv-km" type="number" min="0" className="ta-svc-input" value={form.kilometres} onChange={(e) => set("kilometres", e.target.value)} /></FieldLabel>
          <Select cols={4} label="Body" testid="inv-body" value={form.body_type} onChange={(v) => set("body_type", v)} options={BODY_OPTS} />
          <Select cols={4} label="Transmission" testid="inv-trans" value={form.transmission} onChange={(v) => set("transmission", v)} options={TRANS_OPTS} />

          <Select cols={4} label="Fuel" testid="inv-fuel" value={form.fuel} onChange={(v) => set("fuel", v)} options={FUEL_OPTS} />
          <Select cols={4} label="Rego" testid="inv-rego" value={form.rego_status} onChange={(v) => set("rego_status", v)} options={REGO_OPTS} />
          <FieldLabel cols={4} label="VIN"><input data-testid="inv-vin" className="ta-svc-input" value={form.vin} onChange={(e) => set("vin", e.target.value)} /></FieldLabel>

          {form.status === "rent" && (
            <>
              <FieldLabel cols={3} label="Weekly rate (optional)">
                <input data-testid="inv-weekly" type="number" min="0" className="ta-svc-input" value={form.weekly_rate} onChange={(e) => set("weekly_rate", e.target.value)} />
              </FieldLabel>
              <FieldLabel cols={3} label="Bond (refundable, AUD)">
                <input data-testid="inv-bond" type="number" min="0" className="ta-svc-input" value={form.bond_amount} onChange={(e) => set("bond_amount", e.target.value)} />
              </FieldLabel>
              <FieldLabel cols={3} label="Min hire days">
                <input data-testid="inv-min-days" type="number" min="1" max="365" className="ta-svc-input" value={form.min_rent_days} onChange={(e) => set("min_rent_days", e.target.value)} />
              </FieldLabel>
              <FieldLabel cols={3} label="Max hire days (optional)">
                <input data-testid="inv-max-days" type="number" min="1" max="365" className="ta-svc-input" value={form.max_rent_days} onChange={(e) => set("max_rent_days", e.target.value)} />
              </FieldLabel>
            </>
          )}

          <FieldLabel cols={12} label="Description">
            <textarea data-testid="inv-desc" className="ta-svc-input min-h-[120px]" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Honest description of the car. Service history, recent work, features, anything special." />
          </FieldLabel>

          {!isNew && (
            <>
              <FieldLabel cols={4} label="Sold">
                <label className="inline-flex items-center gap-2 text-zinc-300 text-xs font-mono uppercase tracking-widest pt-2">
                  <input data-testid="inv-is-sold" type="checkbox" checked={!!form.is_sold} onChange={(e) => set("is_sold", e.target.checked)} className="accent-brand-red" />
                  Mark as sold
                </label>
              </FieldLabel>
              <FieldLabel cols={4} label="Published">
                <label className="inline-flex items-center gap-2 text-zinc-300 text-xs font-mono uppercase tracking-widest pt-2">
                  <input data-testid="inv-is-published" type="checkbox" checked={form.is_published !== false} onChange={(e) => set("is_published", e.target.checked)} className="accent-brand-red" />
                  Visible on public site
                </label>
              </FieldLabel>
              <FieldLabel cols={4} label="Feature on home page">
                <label className="inline-flex items-center gap-2 text-zinc-300 text-xs font-mono uppercase tracking-widest pt-2" title="Show this car under the home page hero. Max 4 displayed.">
                  <input data-testid="inv-is-featured" type="checkbox" checked={!!form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} className="accent-brand-red" />
                  Featured
                </label>
              </FieldLabel>
            </>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button data-testid="inv-save" disabled={saving} type="submit" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : isNew ? "Create vehicle" : "Save changes"}
          </button>
          {!isNew && <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Changes are live immediately</span>}
        </div>
      </form>

      {/* Photos block — only when editing an existing vehicle */}
      {!isNew && (
        <section className="border-t border-zinc-900 pt-6">
          <h5 className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red mb-4">Photos ({initial.photos?.length || 0})</h5>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <input
              ref={fileRef}
              data-testid="inv-photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
              className="ta-svc-input file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-brand-red file:text-white file:font-mono file:text-[10px] file:uppercase file:tracking-widest"
            />
            {uploading && <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Uploading…</span>}
          </div>
          {initial.photos?.length ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {initial.photos.map((p) => (
                <figure key={p.id} data-testid={`inv-photo-${p.id}`} className="relative bg-zinc-900 group">
                  <img src={vehiclePhotoUrl(p.url)} alt="" className="w-full h-24 object-cover" />
                  {p.is_cover && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-brand-red text-white font-mono text-[9px] uppercase tracking-widest">
                      <Check className="h-2.5 w-2.5 inline -mt-0.5" /> Cover
                    </span>
                  )}
                  <button data-testid={`inv-photo-del-${p.id}`} onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 h-6 w-6 grid place-items-center bg-black/80 border border-zinc-800 text-zinc-300 hover:text-red-400 opacity-0 group-hover:opacity-100">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </figure>
              ))}
            </div>
          ) : (
            <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">No photos yet — upload at least one to make the listing pop.</div>
          )}
        </section>
      )}
    </div>
  );
}

const FieldLabel = ({ label, children, cols = 6 }) => (
  <label className={`md:col-span-${cols} block`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>
    {children}
  </label>
);

const Select = ({ label, value, onChange, options, testid, cols = 6 }) => (
  <FieldLabel cols={cols} label={label}>
    <select data-testid={testid} className="ta-svc-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </FieldLabel>
);
